import type { PanelComponentSpec } from '@src/types/LayoutConfig';
import { IconName } from '@talos/kyoko';
import { PANEL_COMPONENT_GROUPS } from '../Trading/groups';
import { CustomerAcquisitionLimitMembershipsPanel } from './CustomerAcquisitionLimitMembershipsPanel';
import { CustomerAcquisitionLimitsPanel } from './CustomerAcquisitionLimitsPanel';
import { CustomerAggregationsPanel } from './CustomerAggregationsPanel';
import { CustomerAllInTiersPanel } from './CustomerAllInTiersPanel';
import { CustomerConfigurationsPanel } from './CustomerConfigurationsPanel';
import { CustomerFeeTiersPanel } from './CustomerFeeTiersPanel';
import { CustomerOrderAcceptanceRulesPanel } from './CustomerOrderAcceptanceRulesPanel';
import { CustomerPricingRulesPanel } from './CustomerPricingRulesPanel';
import { CustomerRFQProtocolRulesPanel } from './CustomerRFQProtocolRulesPanel';
import { CustomerSecurityMasterPanel } from './CustomerSecurityMasterPanel';
import { CustomerSubAccountMappingsPanel } from './CustomerSubAccountMappingsPanel';
import { CustomerSymbolGroupsPanel } from './CustomerSymbolGroupsPanel';
import {
  CUSTOMER_AGGREGATIONS_SPACE_PARAMETER_SUPPORT,
  CUSTOMER_ALL_IN_TIERS_SPACE_PARAMETER_SUPPORT,
  CUSTOMER_CONFIGURATIONS_SPACE_PARAMETER_SUPPORT,
  CUSTOMER_FEE_TIERS_SPACE_PARAMETER_SUPPORT,
  CUSTOMER_ORDER_ACCEPTANCE_RULES_SPACE_PARAMETER_SUPPORT,
  CUSTOMER_PRICING_RULES_SPACE_PARAMETER_SUPPORT,
  CUSTOMER_RFQ_PROTOCOL_RULES_SPACE_PARAMETER_SUPPORT,
  CUSTOMER_SECURITY_MASTER_SPACE_PARAMETER_SUPPORT,
  CUSTOMER_SUB_ACCOUNT_MAPPINGS_SPACE_PARAMETER_SUPPORT,
  CUSTOMER_SYMBOL_GROUPS_SPACE_PARAMETER_SUPPORT,
} from './DealerSpaceParameters';

/**
 * Dealer EntityAdmin reference-data panels for Spaces (DEAL-6660).
 *
 * Labels and relative order match Dealer sub-nav leaves in `navigationPaths.ts`.
 * Pricing Tiers is one nav item with Base Tiers / Fee Tiers tabs, so those stay as
 * two panels using the in-page tab names.
 */
export const DEALER_ENTITY_ADMIN_PANEL_COMPONENTS: PanelComponentSpec[] = [
  {
    group: undefined,
    section: 'dealer',
    type: 'customerConfigurations',
    spaceParameterSupport: CUSTOMER_CONFIGURATIONS_SPACE_PARAMETER_SUPPORT,
    label: 'Customers',
    icon: IconName.Counterparty,
    canBeAdded: true,
    minHeight: 200,
    renderer: CustomerConfigurationsPanel,
  },
  {
    group: PANEL_COMPONENT_GROUPS['symbol-setup'],
    section: 'dealer',
    type: 'customerSecurityMaster',
    spaceParameterSupport: CUSTOMER_SECURITY_MASTER_SPACE_PARAMETER_SUPPORT,
    label: 'Security Master',
    icon: undefined,
    canBeAdded: true,
    minHeight: 200,
    renderer: CustomerSecurityMasterPanel,
  },
  {
    group: PANEL_COMPONENT_GROUPS['symbol-setup'],
    section: 'dealer',
    type: 'customerSymbolGroups',
    spaceParameterSupport: CUSTOMER_SYMBOL_GROUPS_SPACE_PARAMETER_SUPPORT,
    label: 'Symbol Groups',
    icon: undefined,
    canBeAdded: true,
    minHeight: 200,
    renderer: CustomerSymbolGroupsPanel,
  },
  {
    group: PANEL_COMPONENT_GROUPS['pricing'],
    section: 'dealer',
    type: 'customerAllInTiers',
    spaceParameterSupport: CUSTOMER_ALL_IN_TIERS_SPACE_PARAMETER_SUPPORT,
    label: 'Base Tiers',
    icon: undefined,
    canBeAdded: true,
    minHeight: 200,
    renderer: CustomerAllInTiersPanel,
  },
  {
    group: PANEL_COMPONENT_GROUPS['pricing'],
    section: 'dealer',
    type: 'customerFeeTiers',
    spaceParameterSupport: CUSTOMER_FEE_TIERS_SPACE_PARAMETER_SUPPORT,
    label: 'Fee Tiers',
    icon: undefined,
    canBeAdded: true,
    minHeight: 200,
    renderer: CustomerFeeTiersPanel,
  },
  {
    group: PANEL_COMPONENT_GROUPS['pricing'],
    section: 'dealer',
    type: 'customerPricingRules',
    spaceParameterSupport: CUSTOMER_PRICING_RULES_SPACE_PARAMETER_SUPPORT,
    label: 'Pricing Rules',
    icon: undefined,
    canBeAdded: true,
    minHeight: 200,
    renderer: CustomerPricingRulesPanel,
  },
  {
    group: PANEL_COMPONENT_GROUPS['pricing'],
    section: 'dealer',
    type: 'customerSubAccountMappings',
    spaceParameterSupport: CUSTOMER_SUB_ACCOUNT_MAPPINGS_SPACE_PARAMETER_SUPPORT,
    label: 'Sub Account Mappings',
    icon: undefined,
    canBeAdded: true,
    minHeight: 200,
    renderer: CustomerSubAccountMappingsPanel,
  },
  {
    group: PANEL_COMPONENT_GROUPS['pricing'],
    section: 'dealer',
    type: 'customerAggregations',
    spaceParameterSupport: CUSTOMER_AGGREGATIONS_SPACE_PARAMETER_SUPPORT,
    label: 'Aggregations',
    icon: undefined,
    canBeAdded: true,
    minHeight: 200,
    renderer: CustomerAggregationsPanel,
  },
  {
    group: PANEL_COMPONENT_GROUPS['execution'],
    section: 'dealer',
    type: 'customerOrderAcceptanceRules',
    spaceParameterSupport: CUSTOMER_ORDER_ACCEPTANCE_RULES_SPACE_PARAMETER_SUPPORT,
    label: 'Order Acceptance Rules',
    icon: undefined,
    canBeAdded: true,
    minHeight: 200,
    renderer: CustomerOrderAcceptanceRulesPanel,
  },
  {
    group: PANEL_COMPONENT_GROUPS['execution'],
    section: 'dealer',
    type: 'customerRFQProtocolRules',
    spaceParameterSupport: CUSTOMER_RFQ_PROTOCOL_RULES_SPACE_PARAMETER_SUPPORT,
    label: 'RFQ Protocol Rules',
    icon: undefined,
    canBeAdded: true,
    minHeight: 200,
    renderer: CustomerRFQProtocolRulesPanel,
  },
  {
    group: PANEL_COMPONENT_GROUPS['net-acquisition'],
    section: 'dealer',
    type: 'customerAcquisitionLimits',
    label: 'Limits',
    icon: undefined,
    canBeAdded: true,
    minHeight: 200,
    renderer: CustomerAcquisitionLimitsPanel,
  },
  {
    group: PANEL_COMPONENT_GROUPS['net-acquisition'],
    section: 'dealer',
    type: 'customerAcquisitionLimitMemberships',
    label: 'Limit Memberships',
    icon: undefined,
    canBeAdded: true,
    minHeight: 200,
    renderer: CustomerAcquisitionLimitMembershipsPanel,
  },
];
